import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useDispatch, useSelector } from 'react-redux';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import CloseIcon from '@material-ui/icons/Close';
import RoutesView from './RoutesView';
import { fetchRouteDetail, clearRoute, selectCurrentRoute } from '../../store/slices/routesSlice';
import { setSidebarVisible, setSidebarTab, setShowDetails } from '../../store/slices/uiSlice';
import { AuthContext } from '../../App';
import { buildRouteLegs, buildMonitoringRouteSummary } from '../../utils/buildRouteLegs';

const API_BASE = '/planner-api';

// Saved-routes browser drawer — anchored at the bottom of the planner so
// the operator can scan hundreds of routes in a dense table without
// leaving the planner. Resizable, persisted height, opens/closes with
// the R hotkey or the floating button.
//
// Click on a row → loads the route into the planner (currentRoute) and
// auto-collapses the drawer so the operator can see the map.

const HEIGHT_KEY = 'planner.routesDrawer.heightPct';
const OPEN_KEY = 'planner.routesDrawer.open';

const MIN_HEIGHT_PCT = 22;
const MAX_HEIGHT_PCT = 65;
const DEFAULT_HEIGHT_PCT = 40;

const useStyles = makeStyles(() => ({
  // Pill button when collapsed — small, doesn't compete with the map.
  collapsedPill: {
    position: 'fixed',
    left: '50%',
    bottom: 8,
    transform: 'translateX(-50%)',
    zIndex: 750,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 10px 3px 8px',
    background: 'linear-gradient(135deg, #1e293b, #334155)',
    color: '#fff',
    borderRadius: 999,
    cursor: 'pointer',
    fontSize: '0.66rem',
    fontWeight: 700,
    lineHeight: 1.2,
    boxShadow: '0 4px 14px rgba(15,23,42,0.32)',
    border: '1px solid rgba(255,255,255,0.08)',
    transition: 'transform 0.15s, box-shadow 0.15s',
    userSelect: 'none',
    '&:hover': {
      transform: 'translateX(-50%) translateY(-1px)',
      boxShadow: '0 6px 18px rgba(15,23,42,0.42)',
    },
  },
  pillBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#3b82f6',
    color: '#fff',
    fontSize: '0.58rem',
    fontWeight: 800,
    padding: '0 6px',
    height: 14,
    borderRadius: 8,
    minWidth: 18,
  },
  drawer: {
    position: 'fixed',
    left: 0, right: 0, bottom: 0,
    zIndex: 700,
    background: '#fff',
    borderTop: '1px solid #e2e8f0',
    boxShadow: '0 -12px 32px rgba(15,23,42,0.18)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'height 0.2s ease',
    overflow: 'hidden',
  },
  // 6 px draggable strip on top — operators can grab and resize.
  resizeHandle: {
    height: 6,
    cursor: 'ns-resize',
    background: 'transparent',
    position: 'relative',
    flexShrink: 0,
    '&:hover::after': { background: '#3b82f6' },
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 2, left: '50%', transform: 'translateX(-50%)',
      width: 60, height: 3, borderRadius: 2,
      background: '#cbd5e1',
      transition: 'background 0.15s',
    },
  },
  header: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 16px',
    background: '#f8fafc',
    borderBottom: '1px solid #e2e8f0',
    flexShrink: 0,
  },
  title: {
    display: 'flex', alignItems: 'center', gap: 8,
    fontSize: '0.86rem', fontWeight: 800, color: '#0f172a',
  },
  body: {
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
}));

export default function RoutesBrowserDrawer() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = React.useContext(AuthContext);
  const currentRoute = useSelector(selectCurrentRoute);

  const [open, setOpen] = useState(() => localStorage.getItem(OPEN_KEY) === 'true');
  const [heightPct, setHeightPct] = useState(() => {
    const saved = parseFloat(localStorage.getItem(HEIGHT_KEY));
    return Number.isFinite(saved) && saved >= MIN_HEIGHT_PCT && saved <= MAX_HEIGHT_PCT
      ? saved
      : DEFAULT_HEIGHT_PCT;
  });
  const [totalRoutes, setTotalRoutes] = useState(null);

  useEffect(() => { localStorage.setItem(OPEN_KEY, String(open)); }, [open]);
  useEffect(() => { localStorage.setItem(HEIGHT_KEY, String(heightPct)); }, [heightPct]);

  // Lightweight count fetch for the collapsed pill — gives the operator a
  // sense of scale ("📚 247 rotas") without opening the drawer.
  useEffect(() => {
    if (!token) return;
    fetch('/planner-api/routes?page=1&limit=1', {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.pagination?.total != null) setTotalRoutes(d.pagination.total);
      })
      .catch(() => {});
  }, [token, open]);

  // Hotkey R — toggle drawer. Skipped while the operator is typing.
  useEffect(() => {
    function onKey(e) {
      const tag = (e.target?.tagName || '').toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable;
      if (isTyping) return;
      if ((e.key === 'r' || e.key === 'R') && !e.ctrlKey && !e.metaKey && !e.altKey) {
        setOpen(o => !o);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Drag-to-resize. We track in viewport-percent so the height scales
  // when the operator resizes the browser window.
  const dragState = useRef(null);
  const onResizeStart = useCallback((e) => {
    dragState.current = {
      startY: e.clientY,
      startHeightPct: heightPct,
      vh: window.innerHeight,
    };
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  }, [heightPct]);
  useEffect(() => {
    function onMove(e) {
      if (!dragState.current) return;
      const dy = dragState.current.startY - e.clientY; // up = grow
      const dpct = (dy / dragState.current.vh) * 100;
      const next = Math.max(MIN_HEIGHT_PCT, Math.min(MAX_HEIGHT_PCT, dragState.current.startHeightPct + dpct));
      setHeightPct(next);
    }
    function onUp() {
      if (!dragState.current) return;
      dragState.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Load a route into the planner. Mirrors what the SavedRoutesQuickPanel
  // does on click — clear, fetch detail, expose details panel and sidebar
  // tab so the operator immediately sees the route loaded.
  const handleSelectRoute = useCallback(async (route) => {
    const id = route?.id || route?.dbId;
    if (!token || !id) return;
    setOpen(false);

    // Limpa estado inspect-mode da rota anterior antes do clearRoute
    // (mesmo padrão do SavedRoutesQuickPanel).
    window.__inspectingRoute = false;
    window.__monitoringOT = null;
    window.__monitoringRouteLegs = null;

    dispatch(clearRoute());
    dispatch(setSidebarVisible(true));
    dispatch(setShowDetails(true));
    dispatch(setSidebarTab(0));

    // Mesma activação inspect-mode da Path B em SavedRoutesQuickPanel
    // — fetchRouteDetail (thunk) + OTs em paralelo, popula globals,
    // dispara highlightOTLeg para o painel inferior aparecer + cores
    // azuis uniformes.
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [routeRes, otsRes] = await Promise.all([
        dispatch(fetchRouteDetail({ token, routeId: id })).unwrap(),
        fetch(`${API_BASE}/transport-orders?assignedRouteId=${id}&pageSize=200`, { headers })
          .then(r => r.ok ? r.json() : null),
      ]);
      if (!routeRes) return;
      const otList = Array.isArray(otsRes)
        ? otsRes
        : (otsRes?.data || otsRes?.items || otsRes?.orders || []);
      if (otList.length === 0) return;

      const { routeLegs, orderedOTIds } = buildRouteLegs(routeRes, otList);
      const synthetic = buildMonitoringRouteSummary(routeRes, otList[0]);

      window.__inspectingRoute = true;
      window.__monitoringOT = synthetic;
      window.__monitoringRouteLegs = routeLegs;
      window.dispatchEvent(new CustomEvent('highlightOTLeg', {
        detail: { orderId: null, orderedOTIds, routeLegs },
      }));
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('fitRouteBounds'));
      }, 600);
    } catch (e) { /* silent */ }
  }, [token, dispatch]);

  if (!open) {
    return (
      <Box className={classes.collapsedPill} onClick={() => setOpen(true)}
        title="Abrir browser de rotas (R)">
        <LibraryBooksIcon style={{ fontSize: 13 }} />
        <span>Rotas Gravadas</span>
        {totalRoutes != null && (
          <span className={classes.pillBadge}>{totalRoutes}</span>
        )}
        <KeyboardArrowUpIcon style={{ fontSize: 13, opacity: 0.6 }} />
      </Box>
    );
  }

  return (
    <Box className={classes.drawer} style={{ height: `${heightPct}vh` }}>
      <Box className={classes.resizeHandle} onMouseDown={onResizeStart} />
      <Box className={classes.header}>
        <Box className={classes.title}>
          <LibraryBooksIcon style={{ fontSize: 18, color: '#3b82f6' }} />
          Rotas Gravadas
          {totalRoutes != null && (
            <span style={{ fontSize: '0.66rem', fontWeight: 700, color: '#fff', background: '#3b82f6', padding: '1px 8px', borderRadius: 10 }}>
              {totalRoutes}
            </span>
          )}
        </Box>
        {currentRoute?.dbId && (
          <Typography style={{ fontSize: '0.66rem', color: '#64748b', marginLeft: 12 }}>
            Rota actual: <b style={{ color: '#0f172a' }}>{currentRoute.name || `#${currentRoute.dbId}`}</b>
          </Typography>
        )}
        <Box style={{ flex: 1 }} />
        <Tooltip title="Atalho: R">
          <Typography style={{ fontSize: '0.6rem', color: '#94a3b8', padding: '2px 8px', border: '1px solid #e2e8f0', borderRadius: 6, fontFamily: 'monospace' }}>R</Typography>
        </Tooltip>
        <IconButton size="small" onClick={() => setOpen(false)} title="Minimizar">
          <KeyboardArrowDownIcon style={{ fontSize: 18, color: '#64748b' }} />
        </IconButton>
        <IconButton size="small" onClick={() => setOpen(false)} title="Fechar">
          <CloseIcon style={{ fontSize: 16, color: '#64748b' }} />
        </IconButton>
      </Box>
      <Box className={classes.body}>
        <RoutesView embedded compact hideMapPreview onSelectRoute={handleSelectRoute} />
      </Box>
    </Box>
  );
}
